import {createSlice} from '@reduxjs/toolkit';

import {olivioLuminaires as luminaires} from './luminaires/olivioLuminaires';
import {olivioLayouts as layouts} from './layouts/olivioLayouts';
import { selectOlivioOffsetFromTop, selectOlivioOffsetFromBottom} from './olivioSelectors';


const maxBranchesAmount = 8;
const customLayoutType = 'custom';
const noLayoutType = 'none';

const singleLumConfig = {
	active: false,
	type: 'luminaire', //luminaire, gobo, speaker, camera
	housingSize: null,
	height: 6,
	la: 0,
	angle: 0,
	pitchOffset: 0,
	// dimmingLevel: null,
	colourTemperature: '3000K',
	lightDistribution: 'Asymmetric',
	// reflectorColor: null,
	accessories: 'None',
	goboAperture: 'normal', // extrawide, wide, normal, long
	spcDecisions: null,
	collisions: [],
};

const olivioInitialState = {
	status: 'idle',
	data: {
		design: 'sistema',
		general: {
			poleHeight: 12,
			numberOfHeads: maxBranchesAmount,
			maxNumberOfHeads: maxBranchesAmount,
			layout: 'spiral',
			heightMax: 11.20,
			heightMin: 6.00,
			angleMax: 315,
			angleMin: 0,
			bracket: 'sistema1',
			poleMaterial: 'steel',
			woodType: 'redwood',
			poleDiameter: '168',
			housingColourInlay: 'SeluxGraphite',
			housingColourPole: 'SeluxGraphite',
			candelabraTop: '3.000',
			base: 'buriedbase',
			separateBuriedBase: 'none',
			secondDoor: 'none',
			fuseBox: 'none',
			buriedBaseSize: 1200,

			housingSize: 'OlivioGrande',
			//housingColour: null,
			lightDistribution: 'Asymmetric',
			colourTemperature: '3000K',
			//dimmingLevel: null,
		},
		heads: [], // set afterwards
		selectedHead: null,
		projectName: '',
		headTableVisible: false,
	},
	error: {},
};

const general = olivioInitialState.data.general;
const firstLayout = layouts[general.layout].calculate(olivioInitialState.data.design, olivioInitialState.data.general);

olivioInitialState.data.heads =  Array.from({length: maxBranchesAmount}, (v, index) => {
	const head = {
		...singleLumConfig
	};
	head.active = index < firstLayout.branches.length;
	const branch = head.active ? firstLayout.branches[index] : {};
	for (const key of Object.keys(head)) {
		if (key in general) {
			head[key] = general[key];
		} else if (key in branch) {
			head[key] = branch[key];
		}
	}
	return head;
});

const userIntendedFeatures = {
	poleHeight: general.poleHeight,
	numberOfHeads: general.numberOfHeads,
	heightMax: general.heightMax,
	heightMin: general.heightMin,
};

const forcedFeatures = {};

function tryRestoringUserIntendedFeatures(oldConfig, newConfig, key, dependencies, maxValue) {
	let restored = false;
	let dependeciesChanged = false;
	for (const depKey of dependencies) {
		if ((depKey === 'heightMin' && newConfig[depKey] < oldConfig[depKey]) || (depKey !== 'heightMin' && newConfig[depKey] > oldConfig[depKey])) {
			dependeciesChanged = true;
			break;
		}
	}
	if (dependeciesChanged) {
		const intendedValue = (key in userIntendedFeatures ? userIntendedFeatures[key] : null);
		if (intendedValue !== null && intendedValue > newConfig[key]) {
			const newValue = Math.min(intendedValue, maxValue);
			// console.log('RESTORING', key, newValue);
			if (newValue < intendedValue) {
				forcedFeatures[key] = newValue;
			}
			restored = true;
			newConfig[key] = newValue;
		}
	}
	return restored;
}


function applyLayoutToHeads(heads, layout) {
	for (let i = 0; i < heads.length; i++) {
		const head = heads[i];
		head.active = i < layout.branches.length;
		head.height = head.active ? layout.branches[i].height : singleLumConfig.height;
		head.angle = head.active ? layout.branches[i].angle : singleLumConfig.angle;
	}
}


function recalculateLayout(data, oldConfig, newConfig, heads) {
	if (newConfig.layout === noLayoutType) {
		return;
	}
	const DesignClass = luminaires[data.design];
	if (newConfig.layout !== customLayoutType) {
		let layout = layouts[newConfig.layout].calculate(data.design, newConfig);
		data.general.maxNumberOfHeads = layout.maxNumberOfHeads;
		if (layout.maxNumberOfHeads < newConfig.numberOfHeads) {
			forcedFeatures.numberOfHeads = layout.branches.length;
			newConfig.numberOfHeads = layout.branches.length;
		} else if (layout.maxNumberOfHeads > newConfig.numberOfHeads) {
			const restored = tryRestoringUserIntendedFeatures(oldConfig, newConfig, 'numberOfHeads', ['poleHeight', 'heightMin', 'heightMax'], layout.maxNumberOfHeads);
			if (restored) {
				layout = layouts[newConfig.layout].calculate(data.design, newConfig);
				newConfig.numberOfHeads = layout.branches.length;
			}
		}
		applyLayoutToHeads(heads, layout);
	} else {
		for (let i = 0; i < heads.length; i++) {
			const head = heads[i];
			head.active = i < newConfig.numberOfHeads;
			head.height = head.active ? Math.min(DesignClass.poleSizes[DesignClass.getPoleId(newConfig)].maxHeight, head.height) : singleLumConfig.height;
			head.angle = head.active ? head.angle : singleLumConfig.angle;
		}
	}
	if (DesignClass.internalOffsets)	 {
		DesignClass.calculateOffsets(newConfig,	heads, true);
	}

	if (data.selectedHead !== null && data.selectedHead >= newConfig.numberOfHeads) {
		data.selectedHead = 0;
	}
}


function updateHeadHeight(state, index, height) {
	state.data.heads[index].height = height;
	state.data.general.layout = customLayoutType;

	const DesignClass = luminaires[state.data.design];
	if (DesignClass.internalOffsets) {
		const offsets = DesignClass.calculateOffsetsByHeight(
			state.data.general,
			height,
			state.data.heads[index].housingSize ? state.data.heads[index].housingSize : state.data.general.housingSize,
			state.data.heads,
			index
		);
		state.data.heads[index].la = offsets.la;
	}
}


function getClosestValue(invalidValue, values) {
	let diff = null;
	let selectedValue = null;
	for (const value of values) {
		if (value !== invalidValue) {
			const newDiff = Math.abs(value - invalidValue);
			if (selectedValue === null || newDiff <= diff) {
				selectedValue = value;
				diff = newDiff;
			}
		}
	}
	return selectedValue;
}

function enforceConstraints(state) {
	const data = state.data;
	const changed = {};
	const designName = data.design;
	const DesignClass = luminaires[designName];
	if ('poleConstraints' in DesignClass) {
		const constraints = DesignClass.poleConstraints;
		const poleMaterial = data.general.poleMaterial;
		let poleHeight = +data.general.poleHeight;
		let poleDiameter = +data.general.poleDiameter;
		if (poleMaterial in constraints) {
			const entry = constraints[poleMaterial];
			if ('poleHeight' in entry && !entry.poleHeight.includes(poleHeight)) {
				poleHeight = 'poleHeightFallback' in entry ? entry.poleHeightFallback : getClosestValue(poleHeight, entry.poleHeight);
			}
			if ('poleDiameter' in entry) {
				const diameterEntry = entry.poleDiameter[poleHeight];
				if (!diameterEntry.includes(poleDiameter)) {
					poleDiameter = 'poleDiameterFallback' in entry ? entry.poleDiameterFallback : getClosestValue(poleDiameter, diameterEntry);
				}
			}
		}
		if (poleHeight !== data.general.poleHeight) {
			changed.poleHeight = data.general.poleHeight;
			data.general.poleHeight = poleHeight;

			const candidateHeightMax = poleHeight - selectOlivioOffsetFromTop(state);
			const candidateHeightMin = Math.min(selectOlivioOffsetFromBottom(state), candidateHeightMax);
			if (data.general.heightMax > candidateHeightMax || data.general.heightMin < candidateHeightMin) {
				changed.heightMax = data.general.heightMax;
				changed.heightMin = data.general.heightMin;
				data.general.heightMax = candidateHeightMax;
				data.general.heightMin = candidateHeightMin;
			}
		}
		if (poleDiameter !== data.general.poleDiameter) {
			changed.poleDiameter = data.general.poleDiameter;
			data.general.poleDiameter = poleDiameter;
		}
	}
	return changed;
}


export const olivioSlice = createSlice({
	name: 'olivio',
	initialState: olivioInitialState,
	reducers: {
		setOlivioDesign: (state, action) => {
			const currentDesign = state.data.design;
			const newDesign = action.payload.id;
			const isChanged = currentDesign !== newDesign;
			if (isChanged) {
				state.data.design = newDesign;
				Object.assign(state.data.general, luminaires[newDesign].defaultConfig);
				const DesignClass = luminaires[newDesign];
				let offsets = [];
				if (!DesignClass.singleBracket && DesignClass.internalOffsets) {
					offsets = DesignClass.calculateOffsets(state.data.general, state.data.heads);
				}
				for (let i = 0; i < state.data.heads.length; i++) {
					state.data.heads[i].active = i < state.data.general.numberOfHeads;
					state.data.heads[i].collisions = [];
					if (offsets.length > i) {
						state.data.heads[i].la = offsets[i].la;
					}
				}
				if (luminaires[newDesign].supportLayouts) {
					recalculateLayout(state.data, state.data.general, state.data.general, state.data.heads);
				}
			}
		},
		setPoleHeight: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general; // shortcut

			const poleHeight = +action.payload.id;
			state.data.general.poleHeight = poleHeight;
			const changed = enforceConstraints(state);
			userIntendedFeatures.poleHeight = poleHeight;
			delete forcedFeatures.poleHeight;
			if ('heightMax' in changed) {
				forcedFeatures.heightMax = state.data.general.heightMax;
				forcedFeatures.heightMin = state.data.general.heightMin;
			}

			const distance = poleHeight - state.data.general.heightMin;

			// when changing pole, force heightmin to have some space for heads
			if (distance <= 2) {
				state.data.general.heightMin = Math.max(3, poleHeight - 2);
			}

			if (luminaires[state.data.design].supportLayouts) {
				tryRestoringUserIntendedFeatures(oldGeneralConfig, generalConfig, 'heightMax', ['poleHeight'], generalConfig.poleHeight);
			}
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setNumberOfHeads: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			generalConfig.numberOfHeads = action.payload.id;
			userIntendedFeatures.numberOfHeads = generalConfig.numberOfHeads;
			delete forcedFeatures.numberOfHeads;
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setHousingSize: (state, action) => {
			state.data.general.housingSize = action.payload.id;
		},
		setBracket: (state, action) => {
			state.data.general.bracket = action.payload.id;
		},
		setPoleMaterial: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			state.data.general.poleMaterial = action.payload.id;
			// enforce appropriate pole heights for material
			if (action.payload.id === 'wood_cylindrical') {
				state.data.general.poleHeight = Math.min(Math.max(state.data.general.poleHeight, 3), 7);
			}
			if (action.payload.id === 'wood_conical' || action.payload.id === 'steel') {
				state.data.general.poleHeight = Math.min(Math.max(state.data.general.poleHeight, 5), 12);
			}
			const changed = enforceConstraints(state);

			const generalConfig = state.data.general; // shortcut
			userIntendedFeatures.poleHeight = generalConfig.poleHeight;
			delete forcedFeatures.poleHeight;

			if ('heightMax' in changed) {
				forcedFeatures.heightMax = state.data.general.heightMax;
				forcedFeatures.heightMin = state.data.general.heightMin;
			}
			if (luminaires[state.data.design].supportLayouts) {
				tryRestoringUserIntendedFeatures(oldGeneralConfig, generalConfig, 'heightMax', ['poleHeight'], generalConfig.poleHeight);
			}
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setWoodType: (state, action) => {
			state.data.general.woodType = action.payload.id;
		},
		setPoleDiameter: (state, action) => {
			state.data.general.poleDiameter = action.payload.id;
		},
		setBase: (state, action) => {
			state.data.general.base = action.payload.id;
		},
		setSeparateBuriedBase: (state, action) => {
			state.data.general.separateBuriedBase = action.payload.id;
		},
		setSecondDoor: (state, action) => {
			state.data.general.secondDoor = action.payload.id;
		},
		setFuseBox: (state, action) => {
			state.data.general.fuseBox = action.payload.id;
		},
		setBuriedBaseSize: (state, action) => {
			state.data.general.buriedBaseSize = action.payload.id;
		},
		setHousingColourInlay: (state, action) => {
			state.data.general.housingColourInlay = action.payload.id;
		},
		setHousingColourPole: (state, action) => {
			state.data.general.housingColourPole = action.payload.id;
		},
		setLayout: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.layout = action.payload.id;

			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setMaterial: (state, action) => {
			state.data.general.material = action.payload.id;
		},
		setOlivioData: (state, action) => {
			// console.log('setOlivioData', action.payload);
			state.data = action.payload;
		},
		setMaxHeight: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			generalConfig.heightMax = action.payload.value;
			// ensure distance between min and max
			generalConfig.heightMin = Math.min(generalConfig.heightMin, generalConfig.heightMax - 1);
			userIntendedFeatures.heightMax = generalConfig.heightMax;
			userIntendedFeatures.heightMin = generalConfig.heightMin;
			delete forcedFeatures.heightMax;
			delete forcedFeatures.heightMin;

			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}

		},
		setMinHeight: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			generalConfig.heightMin = action.payload.value;
			// ensure distance between min and max
			generalConfig.heightMax = Math.max(generalConfig.heightMax, generalConfig.heightMin + 1);
			userIntendedFeatures.heightMin = generalConfig.heightMin;
			userIntendedFeatures.heightMax = generalConfig.heightMax;
			delete forcedFeatures.heightMin;
			delete forcedFeatures.heightMax;

			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setMaxAngle: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.angleMax = action.payload.value;
			if (state.data.general.angleMax < state.data.general.angleMin) {
				state.data.general.angleMin = state.data.general.angleMax;
			}
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},
		setMinAngle: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			state.data.general.angleMin = action.payload.value;
			if (state.data.general.angleMax < state.data.general.angleMin) {
				state.data.general.angleMax = state.data.general.angleMin;
			}
			if (luminaires[state.data.design].supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			}
		},

		setSelectedHead: (state, action) => {
			state.data.selectedHead = action.payload.selectedHead === null ? null : Math.max(0, Math.min(+action.payload.selectedHead, state.data.general.numberOfHeads - 1));
		},

		setSelectedHeadHeight: (state, action) => {
			const selectedHead = state.data.selectedHead;
			if (selectedHead !== null) {
				updateHeadHeight(state, selectedHead, action.payload.value);
			}
		},

		setOlivioHeadHeight: (state, action) => {
			updateHeadHeight(state, action.payload.instance, action.payload.value);
		},

		// update data of a single head
		setOlivioHeadData: (state, action) => {
			Object.assign(state.data.heads[action.payload.instance], action.payload.value);

			const DesignClass = luminaires[state.data.design];
			if (!DesignClass.singleBracket && DesignClass.internalOffsets) {
				const offsets = DesignClass.calculateOffsetsByHeight(
					state.data.general,
					state.data.heads[action.payload.instance].height,
					state.data.heads[action.payload.instance].housingSize ? state.data.heads[action.payload.instance].housingSize : state.data.general.housingSize,
					state.data.heads,
					action.payload.instance
				);
				state.data.heads[action.payload.instance].la = offsets.la;
			}
		},

		// update data of all heads
		setOlivioHeadsData: (state, action) => {
			const oldGeneralConfig = {...state.data.general};
			const generalConfig = state.data.general;
			let sameHousingSize = 0;
			let housingSize = null;
			action.payload.heads.forEach((head, index) => {
				if ('housingSize' in head) {
					if (housingSize === null || head.housingSize === housingSize) {
						sameHousingSize++;
					}
					housingSize = head.housingSize;
				}
				Object.assign(state.data.heads[index], head);
			});
			// if we set all the single heads' housing size the same value, we set also the global one
			if (sameHousingSize === maxBranchesAmount) {
				generalConfig.housingSize = housingSize;
			}
			const DesignClass = luminaires[state.data.design];

			if (DesignClass.supportLayouts) {
				recalculateLayout(state.data, oldGeneralConfig, generalConfig, state.data.heads);
			} else if (!DesignClass.singleBracket && DesignClass.internalOffsets) {
				DesignClass.calculateOffsets(state.data.general, state.data.heads, true);
			}
		},

		setSelectedHeadAngle: (state, action) => {
			const selectedHead = state.data.selectedHead;
			if (selectedHead !== null) {
				state.data.heads[selectedHead].angle = action.payload.value;
				state.data.general.layout = customLayoutType;
			}
		},
		setOlivioHeadAngle: (state, action) => {
			state.data.heads[action.payload.instance].angle = action.payload.value;
			state.data.general.layout = customLayoutType;
		},
		setOlivioHeadHousingSize: (state, action) => {
			state.data.heads[action.payload.instance].housingSize = action.payload.value;
			if (
				(action.payload.value === 'OlivioMedio' || action.payload.value === 'OlivioPiccolo') &&
				(state.data.heads[action.payload.instance].type === 'camera' || state.data.heads[action.payload.instance].type === 'gobo')) {
				state.data.heads[action.payload.instance].type = 'luminaire';
			}
		},
		setOlivioHeadColourTemperature: (state, action) => {
			state.data.heads[action.payload.instance].colourTemperature = action.payload.value;
		},
		setOlivioHeadLightDistribution: (state, action) => {
			state.data.heads[action.payload.instance].lightDistribution = action.payload.value;
		},
		setOlivioHeadType: (state, action) => {
			state.data.heads[action.payload.instance].type = action.payload.value;
		},
		setOlivioAccessories: (state, action) => {
			state.data.heads[action.payload.instance].accessories = action.payload.value;
		},
		setOlivioHeadGoboAperture: (state, action) => {
			state.data.heads[action.payload.instance].goboAperture = action.payload.value;
		},
		setOlivioCandelabraTop: (state, action) => {
			state.data.general.candelabraTop = action.payload.id;
			const amount = +(action.payload.id.substring(0, 1));
			state.data.general.numberOfHeads = amount;

			for (let i = 0; i < state.data.heads.length; i++) {
				state.data.heads[i].active = i < state.data.general.numberOfHeads;
			}
		},
		setHeadTableVisible: (state, action) => {
			state.data.headTableVisible = action.payload.headTableVisible;
		},
	},
	extraReducers: (builder) => {
	}
});

export const {
	setOlivioDesign,
	setPoleHeight,
	setNumberOfHeads,
	setBracket,
	setHousingColourInlay,
	setHousingColourPole,
	setLayout,
	setMaterial,
	setOlivioData,
	setMaxHeight,
	setMinHeight,
	setMaxAngle,
	setMinAngle,
	setSelectedHead,
	setSelectedHeadHeight,
	setSelectedHeadAngle,
	setOlivioHeadHeight,
	setOlivioHeadAngle,
	setOlivioHeadData,
	setOlivioHeadsData,
	setOlivioHeadHousingSize,
	setOlivioHeadColourTemperature,
	setOlivioHeadLightDistribution,
	setOlivioHeadType,
	setOlivioHeadCap,
	setOlivioHeadGoboAperture,
	setHousingSize,
	setPoleMaterial,
	setPoleDiameter,
	setWoodType,
	setOlivioCandelabraTop,
	setBase,
	setSeparateBuriedBase,
	setBuriedBaseSize,
	setSecondDoor,
	setHeadTableVisible
} = olivioSlice.actions;


export default olivioSlice.reducer;
